import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '../../../../../components/Common/UI/Card';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../components/Common/UI/Button';
import { cn } from '../../../../../utils/styles';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../../../components/Common/UI/Form';
import { Input } from '../../../../../components/Common/UI/Input';
import Checkbox from '../../../../../components/Common/UI/Checkbox';
import Label from '../../../../../components/Common/UI/Label';
import { RadioGroup, RadioGroupItem } from '../../../../../components/Common/UI/Radio';
import { notifySuccess } from '../../../../../components/Common/UI/Notify';
import { infoTracUser } from '../../../../../contstant';

const formSchema = z.object({
  email: z.string().refine(value => value === infoTracUser.email, {
    message: 'Incorrect email',
  }),
  token: z.string(),
  // token: z.string()
  //   .min(5, { message: tokenMessage }).max(5, { message: tokenMessage })
  //   .regex(/^\S+$/g, { message: 'Password must not contain spaces.' }),
});

const defaultValues = {
  email: infoTracUser.email,
  token: '',
};

type RadioType = 'email' | 'sms';

function SignInInfoTracContent() {
  const navigate = useNavigate();
  const [radioState, setRadioState] = useState<RadioType>('email');
  const [tokenSent, setTokenSent] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const emailWatch = form.watch('email');
  const tokenWatch = form.watch('token');

  function onSubmit(_values: z.infer<typeof formSchema>) {
    setTokenSent(true);
  }

  return (
    <div className="text-white">
      <header className="max-w-6xl mb-6 mx-auto px-4 flex flex-col gap-4 items-start text-white">
        <div className="h-20 flex gap-2 items-center text-xl">
          <Icons.InfoImageLogo />
          <div className="flex gap-0.5">
            <span className="italic font-semibold">Info</span>
            <span className="font-extrabold">IMAGE</span>
          </div>
        </div>

        <div>
          <Button variant="ghost" size="auto" onClick={() => navigate(-1)}>
            <Icons.ArrowLeft className="rotate-180 child:opacity-100 text-lg" /> Back
          </Button>
        </div>
      </header>

      <div className="flex justify-center items-center m-auto">
        <Card className="bg-[#FFFFFF1A] max-w-6xl w-full py-12 px-[70px] flex flex-col gap-4">
          <div id="card-header" className="flex gap-3 items-center mb-2">
            <Icons.InfoTracLogo />
            <span className="text-lg tracking-wide">InfoTRAC</span>
          </div>
          <div>
            <h3 className="text-[26px] font-bold">Dual Authentication Check</h3>
            <p>
              Our system detects that is the first time you use this device to visit us. For security purposes, we must
              confirm your identity. Choose how you would like to receive the identity verification code and click
              “Submit”. You will see the identification code you receive.
            </p>
          </div>

          <CardContent className="p-0 flex flex-col gap-4">
            <RadioGroup
              defaultValue="email"
              className="flex gap-4"
              value={radioState}
              onValueChange={val => setRadioState(val as RadioType)}
            >
              {[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'Sms' },
              ].map(({ value, label }) => (
                <div key={value} className="flex items-center space-x-2 child:text-white child:m-0 child:text-[18px]">
                  <RadioGroupItem
                    value={value}
                    id={`option-${value}`}
                    className="bg-[#8794AC]"
                    disabled={value === 'sms'}
                  />
                  {/** If sms - then disabled */}
                  <Label htmlFor={`option-${value}`} className={cn({ '!text-[#8794AC]': value === 'sms' })}>
                    {label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                <div className="flex gap-4">
                  <FormField
                    name="email"
                    render={({ field }) => (
                      <FormItem className="grow max-w-sm">
                        <FormControl>
                          <Input placeholder="Email" variant="home" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    variant="link"
                    size="auto"
                    className="text-[#02B3CB]"
                    onClick={() => form.setValue('email', defaultValues.email)}
                  >
                    Reset Email
                  </Button>
                </div>
                <Button
                  type="submit"
                  variant="default"
                  className="grow max-w-sm disabled:bg-[#8794AC]"
                  disabled={!emailWatch}
                >
                  Send Token
                </Button>

                {tokenSent ? (
                  <>
                    <p className="mt-4">Please enter the token you received below.</p>
                    <div className="flex gap-4">
                      <FormField
                        name="token"
                        render={({ field }) => (
                          <FormItem className="grow max-w-sm">
                            <FormControl>
                              <Input
                                placeholder="Token"
                                variant="home"
                                // onFocus={!tokenWatch ? () => form.setValue('token', '12345') : undefined}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="flex gap-2 items-center child:text-[#02B3CB] child:m-0">
                        <Checkbox id="saveBrowser" defaultChecked />
                        <Label htmlFor="saveBrowser">Save Browser</Label>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      variant="default"
                      disabled={!tokenWatch || tokenWatch.length !== 5}
                      className="disabled:bg-[#8794AC] max-w-sm"
                      onClick={() => {
                        navigate('/infoTrac');
                        notifySuccess('Authorized successfully!');
                      }}
                    >
                      Submit
                    </Button>
                  </>
                ) : null}
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SignInInfoTracContent;
